import React from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { FormikProvider } from 'formik'
import { useTranslation } from 'react-i18next'
import {
  Modal,
  ActionModal,
  Text,
  AcknowledgeModal,
  TextInput,
  FormField,
  Spacer,
  Select,
} from '@percent/lemonade'
import type { InviteUserModalProps } from './InviteUserModal.types'

export function InviteUserModal({
  isOpen,
  onClose,
  onConfirm,
  state,
  errorMessage,
  employeeTypeOptions,
}: InviteUserModalProps) {
  const { t } = useTranslation()

  const formik = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      email: '',
      fullName: '',
      employeeType: '',
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email(t('modal.accounts.inviteUsers.email.validation.invalid'))
        .required(t('modal.accounts.inviteUsers.email.validation.required')),
      fullName: Yup.string()
        .trim()
        .min(1, t('modal.accounts.inviteUsers.fullName.validation.required'))
        .required(t('modal.accounts.inviteUsers.fullName.validation.required')),
      employeeType: Yup.string().required(
        t('modal.accounts.inviteUsers.employeeType.validation.required')
      ),
    }),
    onSubmit: async (values) => {
      onConfirm(values)
    },
  })

  const {
    errors,
    values,
    touched,
    dirty,
    handleChange,
    handleBlur,
    handleSubmit,
    isValid,
    resetForm,
  } = formik

  const handleClose = () => {
    resetForm()
    onClose()
  }

  if (!isOpen) return null

  if (state === 'success') {
    return (
      <Modal open={isOpen} onClose={handleClose}>
        <AcknowledgeModal
          result="positive"
          title={t('modal.accounts.inviteUsers.success.title')}
          description={t('modal.accounts.inviteUsers.success.description', {
            email: values.email,
          })}
          buttonText={t('modal.accounts.inviteUsers.button.close')}
          handleClose={handleClose}
        />
      </Modal>
    )
  }

  if (state === 'error') {
    return (
      <Modal open={isOpen} onClose={handleClose}>
        <AcknowledgeModal
          result="negative"
          title={t('modal.accounts.inviteUsers.error.title')}
          description={
            errorMessage || t('modal.accounts.inviteUsers.error.description')
          }
          buttonText={t('modal.accounts.inviteUsers.button.close')}
          handleClose={handleClose}
        />
      </Modal>
    )
  }

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <ActionModal
        title={t('modal.accounts.inviteUsers.title')}
        primaryButtonText={t(
          'modal.accounts.inviteUsers.button.sendInvitation'
        )}
        secondaryButtonText={t('modal.accounts.inviteUsers.button.cancel')}
        type="submit"
        variant={isValid && dirty ? 'primary' : 'secondary'}
        disabled={!(isValid && dirty)}
        handleClose={handleClose}
        handleSubmit={handleSubmit}
        primaryBtnTestId="invite-user-submit-button"
        secondaryBtnTestId="invite-user-cancel-button"
        aria-labelledby="invite-user-form-modal"
      >
        <Text>{t('modal.accounts.inviteUsers.description')}</Text>
        <Spacer size={4} axis="vertical" />
        <form>
          <FormikProvider value={formik}>
            <FormField
              label={t('modal.accounts.inviteUsers.email.label')}
              status={touched.email && errors.email ? 'danger' : 'default'}
              statusMessage={errors.email}
              necessity="required"
              data-testid="email"
            >
              <TextInput
                name="email"
                value={values.email}
                placeholder={t('modal.accounts.inviteUsers.email.placeholder')}
                type="email"
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </FormField>
            <Spacer size={4} axis="vertical" />
            <FormField
              label={t('modal.accounts.inviteUsers.fullName.label')}
              status={
                touched.fullName && errors.fullName ? 'danger' : 'default'
              }
              statusMessage={errors.fullName}
              necessity="required"
              data-testid="fullName"
            >
              <TextInput
                name="fullName"
                value={values.fullName}
                placeholder={t(
                  'modal.accounts.inviteUsers.fullName.placeholder'
                )}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </FormField>
            <Spacer size={4} axis="vertical" />
            <FormField
              label={t('modal.accounts.inviteUsers.employeeType.label')}
              status={
                touched.employeeType && errors.employeeType
                  ? 'danger'
                  : 'default'
              }
              statusMessage={errors.employeeType}
              necessity="required"
              data-testid="employeeType"
            >
              <Select
                name="employeeType"
                options={employeeTypeOptions}
                placeholder={t(
                  'modal.accounts.inviteUsers.employeeType.placeholder'
                )}
                onChange={(option) => {
                  formik.setFieldValue('employeeType', option.value)
                }}
                defaultValue={employeeTypeOptions.find(
                  (option) => option.value === values.employeeType
                )}
              />
            </FormField>
          </FormikProvider>
        </form>
      </ActionModal>
    </Modal>
  )
}
